import { useEffect, useMemo, useState } from 'react'
import { fetchHubProjects } from '@/services/hubProjects'

/**
 * Heuristic weights (v2, 2026-04) — score = impact − effort (higher = better to do next).
 * Tune with real portfolios; constants kept explicit for later calibration.
 */
const IMPACT = {
  priorityScale: 2,
  status: { active: 1, idea: 0, archive: -1 },
}

const EFFORT = {
  base: { active: 4, idea: 6, archive: 2 },
  /** Missing fields — active work should document vision sooner than early ideas. */
  visionIfMissing: { active: 2, idea: 1, default: 2 },
  descIfMissing: 1,
  stackIfMissing: 2,
}

function estimateImpact(project) {
  const priority = Number(project.priority) || 3
  const st = project.status
  const statusBonus = st === 'active' ? IMPACT.status.active : st === 'idea' ? IMPACT.status.idea : IMPACT.status.archive
  return Math.max(1, Math.min(10, priority * IMPACT.priorityScale + statusBonus))
}

function estimateEffort(project) {
  const st = project.status === 'active' ? 'active' : project.status === 'idea' ? 'idea' : 'archive'
  const visionMissing = project.vision ? 0 : EFFORT.visionIfMissing[st] ?? EFFORT.visionIfMissing.default
  const descMissing = project.description ? 0 : EFFORT.descIfMissing
  const stackMissing = Array.isArray(project.tech_stack) && project.tech_stack.length ? 0 : EFFORT.stackIfMissing
  const base = EFFORT.base[st] ?? EFFORT.base.archive
  return Math.max(1, Math.min(10, base + visionMissing + descMissing + stackMissing))
}

function nextSteps(project) {
  const items = []
  if (!project.vision) items.push('Add clear vision/context in editor')
  if (!project.description) items.push('Write concise project description')
  if (!Array.isArray(project.tech_stack) || project.tech_stack.length === 0) {
    items.push('Define initial tech stack tags')
  }
  if (project.status === 'idea') items.push('Run refine and move to active when scoped')
  if (!project.external_links || Object.keys(project.external_links || {}).length === 0) {
    items.push('Attach at least one resource link')
  }
  return items.slice(0, 3)
}

export default function HubEvaluationPage() {
  const [projects, setProjects] = useState([])
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data, error: err } = await fetchHubProjects()
      if (cancelled) return
      if (err) {
        setError(err.message || String(err))
        setProjects([])
        return
      }
      setError('')
      setProjects(data || [])
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const ranked = useMemo(() => {
    return (projects || [])
      .map((p) => {
        const impact = estimateImpact(p)
        const effort = estimateEffort(p)
        return {
          ...p,
          impact,
          effort,
          score: impact - effort,
          suggestions: nextSteps(p),
        }
      })
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score
        if (b.impact !== a.impact) return b.impact - a.impact
        const pb = Number(b.priority) || 0
        const pa = Number(a.priority) || 0
        if (pb !== pa) return pb - pa
        const tb = new Date(b.updated_at || 0).getTime()
        const ta = new Date(a.updated_at || 0).getTime()
        return tb - ta
      })
  }, [projects])

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-lg font-semibold tracking-tight">Impact vs effort</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Ranked by <span className="font-mono text-xs">score = impact − effort</span> (1–10 each). Impact uses
          priority + status; effort penalises missing vision/description/stack. Tie-break: score → impact → priority →
          recency.
        </p>
      </div>
      {error ? (
        <p className="text-sm text-red-400">{error}</p>
      ) : ranked.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No projects available for evaluation.
        </div>
      ) : (
        <div className="space-y-4">
          {ranked.map((p) => (
            <article key={p.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold">{p.name}</h2>
                  <p className="font-mono text-[11px] text-muted-foreground">{p.slug}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Score</p>
                  <p className="font-mono text-sm">{p.score}</p>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                <div className="rounded border border-border bg-muted/30 p-2">
                  <p className="text-muted-foreground">Impact</p>
                  <p className="font-mono">{p.impact}/10</p>
                </div>
                <div className="rounded border border-border bg-muted/30 p-2">
                  <p className="text-muted-foreground">Effort</p>
                  <p className="font-mono">{p.effort}/10</p>
                </div>
                <div className="rounded border border-border bg-muted/30 p-2">
                  <p className="text-muted-foreground">Priority</p>
                  <p className="font-mono">{p.priority ?? '—'}</p>
                </div>
                <div className="rounded border border-border bg-muted/30 p-2">
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-mono">{p.status}</p>
                </div>
              </div>

              {p.suggestions.length > 0 ? (
                <div className="mt-3 border-t border-border pt-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Next steps</p>
                  <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground">
                    {p.suggestions.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  )
}
